/**
 * useActiveOrders — 진행 중(non-terminal) 주문 추적 훅
 *
 * `/orders` 탭의 OrdersContent 가 표시할 활성 주문(픽업+테이블) 목록을 제공.
 *
 * ## #659: 3소스 합성 — order-history 와 데이터 소스 공유 (방향 (b))
 * 기존에는 자격증명 소스(auth Sanctum / guest phone+password)만 사용해
 * 전화 없는 테이블 게스트(#647 — 빈 phone 자격증명 저장 차단)는 영구 disabled 였다.
 * order-history 가 쓰는 소스를 공유 합성하되 **non-terminal 필터**만 적용한다:
 *
 *   A. 자격증명 조회 (기존):
 *      - 로그인(`mode='auth'`): GET /api/v1/orders (Sanctum, 활성 status 별 조회)
 *      - 비회원(`mode='guest'` + 유효 phone): POST /api/v1/orders/guest/lookup
 *      - phone 이 비어있으면(전화 없는 테이블 게스트) 에러가 아니라 **소스 부재**로
 *        조용히 skip — '자격증명 필요' 오안내 제거 (#659)
 *   B. 테이블 세션 토큰 조회: sessionStorage 테이블 세션(3키) 존재 시
 *      GET /table-sessions/{token}/unpaid-postpay (admin R5) — 응답 orders 는 표준
 *      OrderResource(Order[]) 형태. 비회원은 X-Guest-Password 헤더 — guestCredentials
 *      부재 시 **보존 password(`olulo_guest_password`, utils/password) 폴백** (#647 이
 *      보존하는 password 출처와 연결: 전화 없는 테이블 게스트도 401/403 으로 credentials
 *      가 비워진 뒤에도 세션 소스가 동작해야 함).
 *      **회원(auth) 모드는 세션 소스 skip** (codex P2): 회원 주문은 A 소스가 전부 권위
 *      있게 커버하고, 타인/혼재 세션 토큰 잔존 시 서버 403(IDOR 로깅) 노이즈만 생긴다.
 *   C. 로컬 주문 스냅샷 폴백 (OrderHistoryContext): **자격증명 소스가 부재/실패일 때만**
 *      후보(DB id 보유 + 24h 이내, 최대 10건)를 POST /orders/{id}/status 로 서버 검증해
 *      활성(is_active) 주문만 노출. 검증 실패(403 등)는 fail-closed 미노출.
 *
 * ## 서버 인가 경계 (codex 리뷰 오인 차단 — admin ResolvesTableSessionChargeAccess 실측)
 * 세션 토큰 소스(B)의 GET unpaid-postpay 는 admin 이 다음 인가 경계로 보호한다:
 *   - 회원 모드: 세션의 **전 주문이 그 회원 customer_id 소유**가 아니면 403
 *     (비회원 주문이 섞여만 있어도 403 + IDOR 로깅).
 *   - 비회원 모드: 전 주문 **동일 guest_password 매칭** + lockout(5회).
 * 따라서 "타인 세션 토큰 잔존" 류 조합은 서버가 403 으로 차단하고 FE 는 allSettled
 * 실패로 미표시한다 — FE 에서 세션 토큰만으로 타인 주문이 노출될 수 없다.
 *
 * ## 인증 필요 빈 상태 (#659 codex Important — silent empty 금지)
 * 표시할 주문이 0건인데 그 원인이 인가 거절(세션 소스 401/403, 스냅샷 검증 401/403)이면
 * `authRequired=true` 를 노출한다 — UI 가 일반 빈 상태/에러와 구분된
 * "주문을 확인하려면 인증이 필요합니다" 안내를 표시한다 (silent pass 방지).
 *
 * 합성 원칙 (서버 권위):
 *   - 같은 주문이 스냅샷+서버 조회 양쪽에 있으면 **서버 우선** (스냅샷은 서버 결과에
 *     없는 id 만 검증 대상).
 *   - 로컬 스냅샷도 표시 전 반드시 서버 status 검증을 통과해야 함 — 스냅샷 단독으로
 *     상태를 추정하지 않는다 (terminal 주문 영구 노출 차단).
 *   - 모든 소스에 동일한 ACTIVE_ORDER_STATUSES 필터 적용 (terminal 비노출 SSOT).
 *
 * 에러 노출: 시도한 서버 소스가 **전부** 실패하고 표시할 주문도 없을 때만 error.
 * 일부 소스 실패는 warn 로깅 후 성공 소스 결과를 표시한다 (부분 성공 우선).
 *
 * BE OrderController::index / guestLookup 모두 customer 매칭으로 pickup/table 을
 * 모두 반환 — order_type 필터는 적용하지 않는다 (사용자 의도 = 통합 표시).
 */
import { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { addBreadcrumb } from '@sentry/react';
import { orderApi, ApiClientError } from '@/lib/api';
import { tableSessionApi } from '@/lib/api/payments';
import { getTableSessionFromStorage, TABLE_SESSION_CHANGED_EVENT } from '@/lib/tableContext';
import { readComensalAccess, readSplitPaymentAvailable } from '@/lib/comensalAccess';
import { COMENSAL_ACCESS_CHANGED_EVENT } from '@/lib/sessionStorageKeys';
import {
  ACTIVE_ORDER_STATUSES,
  type Order,
  type OrderItemDetail,
  type OrderStatusData,
} from '@/lib/api/types';
import type { OrderHistoryItem } from '@/types';
import { getSavedGuestPassword } from '@/utils/password';
import { logger } from '@/utils/logger';
import { safeErrorMetadata } from '@/utils/safeErrorMetadata';

function recordAuthRead(path: 'batch' | 'legacy', started: number) {
  const duration = performance.now() - started;
  addBreadcrumb({
    category: 'olulo.read-diagnostic', message: 'Active-order status read', level: 'info',
    data: {
      diagnostic_marker: 'menu.orders.active-status.v1', operation: 'authenticated-read', path,
      scheduled_read_count: path === 'batch' ? 1 : 6,
      duration_bucket: duration < 2_000 ? 'lt-2s' : duration < 10_000 ? '2s-to-10s' : 'gte-10s',
    },
  });
}

export type ActiveOrdersMode = 'auth' | 'guest' | 'disabled';

interface GuestCredentials {
  phone: string;
  password: string;
}

interface UseActiveOrdersOptions {
  /** 'auth' | 'guest' | 'disabled' — 자격증명 소스(A) 모드. disabled 여도 B/C 소스는 동작 */
  mode: ActiveOrdersMode;
  /** 로그인 상태를 유지한 계정 전환도 별도 조회 수명으로 구분한다. */
  customerId?: number | null;
  /** mode='guest' 일 때 guestLookup payload + 세션/스냅샷 검증 비밀번호 */
  guestCredentials?: GuestCredentials | null;
  /**
   * #659 로컬 주문 스냅샷 (OrderHistoryContext.orderHistory) — 자격증명 소스 부재/실패 시
   * 서버 status 검증을 거쳐 폴백 노출. 미전달 시 폴백 소스 비활성.
   */
  localSnapshots?: readonly OrderHistoryItem[];
  /** 반복 새로고침 활성화. App 은 `/orders` 탭에서만 켜 API 부하를 제한한다. */
  pollingEnabled?: boolean;
  /** 활성 주문 목록 백그라운드 새로고침 간격(ms). false 또는 0 이하면 비활성화. */
  pollIntervalMs?: number | false;
}

interface UseActiveOrdersReturn {
  orders: Order[];
  loading: boolean;
  error: Error | null;
  /**
   * #659 codex Important: 표시물 0건의 원인이 인가 거절(401/403)일 때 true —
   * UI 가 일반 빈 상태와 구분된 '인증 필요' 안내를 표시한다 (silent empty 금지).
   * 세션 소스 401/403 실패 또는 스냅샷 서버 검증 401/403 거절 시 set.
   */
  authRequired: boolean;
  refetch: () => Promise<void>;
}

/** 인가 거절(401/403) 판정 — authRequired 빈 상태 구분용 (#659) */
function isAuthRejection(reason: unknown): boolean {
  return reason instanceof ApiClientError && (reason.status === 401 || reason.status === 403);
}

const ACTIVE_STATUS_SET = new Set<string>(ACTIVE_ORDER_STATUSES);

/** 로컬 스냅샷 검증 상한 — /orders 진입 시 pollStatus 호출 폭주 차단 */
const LOCAL_SNAPSHOT_VERIFY_LIMIT = 10;
/** 로컬 스냅샷 검증 대상 시간 창 (24h) — 그 이전 주문은 진행 중일 가능성이 사실상 없음 */
const LOCAL_SNAPSHOT_WINDOW_MS = 24 * 60 * 60 * 1000;
/** DB id 보유 스냅샷 식별 패턴 (OrderHistoryContext 가 `order-{dbId}` 로 저장) */
const LOCAL_SNAPSHOT_ID_PATTERN = /^order-(\d+)$/;
/** `/orders` 탭에서 활성 주문 상태를 재검증하는 기본 간격 — API 부하를 피하려고 보수적으로 둔다. */
const ACTIVE_ORDERS_POLL_INTERVAL_MS = 30 * 1000;
/** 백그라운드 인가 거절 반복 시 admin guest lockout(5회)에 닿기 전에 폴링 중단 */
const BACKGROUND_AUTH_REJECTION_POLLING_LIMIT = 1;

function sortNewestFirst(orders: Order[]): Order[] {
  return [...orders].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );
}

function getOrderSnapshotTime(order: Order): number {
  const updatedAt = Date.parse(order.updated_at);
  if (Number.isFinite(updatedAt)) return updatedAt;

  const createdAt = Date.parse(order.created_at);
  return Number.isFinite(createdAt) ? createdAt : Number.NEGATIVE_INFINITY;
}

function keepLatestById(orders: Order[]): Order[] {
  const latestById = new Map<number, Order>();

  for (const order of orders) {
    const previous = latestById.get(order.id);
    if (!previous || getOrderSnapshotTime(order) >= getOrderSnapshotTime(previous)) {
      latestById.set(order.id, order);
    }
  }

  return [...latestById.values()];
}

/**
 * #726: 선불(prepay) 결제 실패 주문은 진행 중 목록에서 제외 (admin #1960 의 고객측 거울상).
 *
 * 신규 prepay 결제실패는 admin #1958 C-4 가 주문을 Cancelled(terminal)로 보상취소하여 자연
 * 제외되나, C-4 이전 레거시 주문은 Pending(active) + `latest_payment.status='failed'` 로
 * 잔존해 "진행 중"에 노출된다(주문 미성립인데 en curso 로 오인). order-history 에는 #723
 * "선불 결제실패" 배지로 유지되므로 active 목록에서만 숨긴다. 후불(postpay) 청구 실패는 식사
 * 후 매장 재징수 대상이라 제외하지 않는다(#1960 과 동일 조건: failed AND NOT postpay).
 */
function isPrepayPaymentFailed(order: Order): boolean {
  const payment = order.latest_payment;

  return !!payment && payment.status === 'failed' && payment.timing !== 'postpay';
}

function keepActiveOrders(orders: Order[]): Order[] {
  return sortNewestFirst(
    keepLatestById(
      orders.filter(
        (order) => ACTIVE_STATUS_SET.has(order.status) && !isPrepayPaymentFailed(order)
      )
    )
  );
}

async function fetchAuthActiveOrders(isCurrent: () => boolean): Promise<Order[]> {
  const started = performance.now();
  try {
    const response = await orderApi.getActiveOrdersBatch();
    if (!isCurrent()) return [];
    recordAuthRead('batch', started);
    if (response.meta.failed_statuses.length > 0) {
      logger.warn('[useActiveOrders] partial status fetch failure:', {
        diagnostic_marker: 'menu.orders.active-status.v1', operation: 'batch-partial', failed_statuses: response.meta.failed_statuses,
      });
    }
    return response.data;
  } catch (error) {
    if (!isCurrent()) return [];
    recordAuthRead('batch', started);
    // Only an unsupported old route gets one bounded read-only compatibility pass.
    // Auth, transport, server and invalid-contract failures retain their original path.
    if (!(error instanceof ApiClientError) || error.status !== 404) throw error;
  }


  const settled = await Promise.allSettled(
    ACTIVE_ORDER_STATUSES.map((status) =>
      orderApi.getOrders({
        status,
        per_page: 50,
      })
    )
  );

  if (!isCurrent()) return [];
  recordAuthRead('legacy', started);

  const failedRequests = settled.flatMap((result, index) =>
    result.status === 'rejected'
      ? [{ status: ACTIVE_ORDER_STATUSES[index], reason: result.reason }]
      : []
  );

  if (failedRequests.length === settled.length) {
    // #659 R3: 전부 인가 거절(401/403)이면 generic Error 로 삼키지 않고 원본
    // ApiClientError 를 전파 — 호출부의 authRequired('인증 필요' 빈 상태) 분기 도달.
    const authRejection = failedRequests.find((failed) => isAuthRejection(failed.reason));
    if (authRejection) {
      throw authRejection.reason;
    }
    throw new Error('활성 주문 조회에 모두 실패했습니다.');
  }

  if (failedRequests.length > 0) {
    logger.warn('[useActiveOrders] partial status fetch failure:', {
      diagnostic_marker: 'menu.orders.active-status.v1', operation: 'legacy-partial', failures: failedRequests.map(({ status, reason }) => ({ status, error: safeErrorMetadata(reason) })),
    });
  }

  return settled.flatMap((result) =>
    result.status === 'fulfilled' ? (result.value.data ?? []) : []
  );
}

/**
 * 로컬 스냅샷 → 검증 후보 선별 (#659 C 소스).
 *
 * - `order-{dbId}` 패턴(서버 DB id 보유)만 — UUID 스냅샷은 서버 검증 불가
 * - 서버 소스(A/B)가 이미 반환한 id 제외 — 서버 우선(중복 병합)
 * - 24h 이내 + 최신순 최대 10건 — pollStatus 폭주/무의미 검증 차단
 */
export function selectLocalSnapshotCandidates(
  snapshots: readonly OrderHistoryItem[],
  excludeIds: ReadonlySet<number>,
  now: number = Date.now()
): Array<{ dbId: number; snapshot: OrderHistoryItem }> {
  const seen = new Set<number>();
  const candidates: Array<{ dbId: number; snapshot: OrderHistoryItem }> = [];

  const newestFirst = [...snapshots].sort((a, b) => b.date.getTime() - a.date.getTime());
  for (const snapshot of newestFirst) {
    const match = LOCAL_SNAPSHOT_ID_PATTERN.exec(snapshot.id);
    if (!match) continue;

    const dbId = Number(match[1]);
    if (!Number.isSafeInteger(dbId) || excludeIds.has(dbId) || seen.has(dbId)) continue;

    const createdAtMs = snapshot.date.getTime();
    if (!Number.isFinite(createdAtMs) || now - createdAtMs > LOCAL_SNAPSHOT_WINDOW_MS) continue;

    seen.add(dbId);
    candidates.push({ dbId, snapshot });
    if (candidates.length >= LOCAL_SNAPSHOT_VERIFY_LIMIT) break;
  }

  return candidates;
}

/**
 * 로컬 스냅샷 + 서버 검증 status → 표시용 Order 합성 (#659 C 소스).
 *
 * status/estimated_ready_time 등 상태 필드는 **서버 검증값**, 라인/금액/매장 필드는
 * 스냅샷 보충 (guest pollStatus 응답에는 라인이 없음 — order-history 의
 * mergeApiOrderWithLocalSnapshot 과 동일한 보충 원칙).
 */
function snapshotToOrder(
  snapshot: OrderHistoryItem,
  dbId: number,
  statusData: OrderStatusData
): Order {
  const items: OrderItemDetail[] = snapshot.items.map((item, index) => ({
    id: index,
    menu_id: 0,
    menu_name: item.name,
    quantity: item.quantity,
    unit_price: item.price,
    total_price: item.price * item.quantity,
  }));

  return {
    id: dbId,
    order_number: statusData.order_number ?? snapshot.orderNumber ?? null,
    store_id: Number.parseInt(snapshot.restaurantId, 10) || 0,
    store_name: snapshot.restaurant,
    order_type: snapshot.type,
    table_number: snapshot.tableNumber ?? null,
    status: statusData.status,
    status_label: statusData.status_label,
    items,
    gross_subtotal: snapshot.grossSubtotal,
    net_subtotal: snapshot.netSubtotal,
    tax: snapshot.tax,
    tip: snapshot.tip,
    total: snapshot.total,
    currency: snapshot.currency ?? 'MXN',
    created_at: snapshot.date.toISOString(),
    updated_at: statusData.updated_at,
    estimated_ready_time: statusData.estimated_ready_time,
    processed_at: statusData.processed_at,
    failure_reason: statusData.failure_reason,
    is_processing: statusData.is_processing,
    is_active: statusData.is_active,
  };
}

/**
 * 로컬 스냅샷 후보를 서버 status 로 검증해 활성 주문만 반환 (#659 C 소스).
 *
 * 검증 실패(403 비밀번호 불일치 / 404 / 네트워크)는 해당 건만 fail-closed 미노출 —
 * 스냅샷 단독 추정으로 terminal 주문이 영구 노출되는 것을 차단한다.
 * 인가 거절(401/403) 건수는 별도 보고 — 표시물 0건 시 '인증 필요' 안내 분기에 사용
 * (codex Important: silent empty 금지).
 */
async function verifyLocalSnapshots(
  candidates: Array<{ dbId: number; snapshot: OrderHistoryItem }>,
  guestPassword: string | undefined,
  isCurrent: () => boolean,
): Promise<{ verified: Order[]; authRejectedCount: number }> {
  const settled = await Promise.allSettled(
    candidates.map(({ dbId }) => orderApi.pollStatus(dbId, guestPassword))
  );

  if (!isCurrent()) return { verified: [], authRejectedCount: 0 };

  const verified: Order[] = [];
  let authRejectedCount = 0;
  settled.forEach((result, index) => {
    if (result.status !== 'fulfilled') {
      if (isAuthRejection(result.reason)) authRejectedCount += 1;
      logger.warn('[useActiveOrders] 로컬 스냅샷 서버 검증 실패 — 해당 건 미노출:', {
        diagnostic_marker: 'menu.orders.active-status.v1', operation: 'snapshot-read', error: safeErrorMetadata(result.reason) ?? { type: 'Unknown' },
      });
      return;
    }

    const statusData = result.value;
    if (!ACTIVE_STATUS_SET.has(statusData.status)) return; // terminal 비노출

    verified.push(snapshotToOrder(candidates[index].snapshot, candidates[index].dbId, statusData));
  });

  return { verified, authRejectedCount };
}

export function useActiveOrders({
  mode,
  customerId,
  guestCredentials,
  localSnapshots,
  pollingEnabled = false,
  pollIntervalMs = ACTIVE_ORDERS_POLL_INTERVAL_MS,
}: UseActiveOrdersOptions): UseActiveOrdersReturn {
  const [orders, setOrders] = useState<Order[]>([]);
  // P0-A 시정: 마운트 직후 loading=true 초기화 → 빈 화면 flash 차단.
  // #659: disabled 모드여도 세션/스냅샷 소스가 동작할 수 있어 항상 true 로 시작 —
  // 소스 전무 판정은 fetchActive 의 early-return 이 즉시 loading=false 로 해제한다.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  // #659 codex Important: 인가 거절로 표시물 0건일 때의 구분 빈 상태 (silent empty 금지)
  const [authRequired, setAuthRequired] = useState(false);
  const isMountedRef = useRef(false);
  const requestIdRef = useRef(0);
  const foregroundRequestRef = useRef<number | null>(null);
  const pollingFetchInFlightRef = useRef<symbol | null>(null);
  const backgroundAuthRejectionCountRef = useRef(0);
  const authRejectedPollingSuspendedRef = useRef(false);
  const [authRejectedPollingSuspended, setAuthRejectedPollingSuspended] = useState(false);
  const [tableSessionRefreshVersion, setTableSessionRefreshVersion] = useState(0);

  // guestCredentials 객체 참조 변동 → 무한 fetch 방지 위해 phone+password 만 추출
  const guestPhone = guestCredentials?.phone ?? null;
  const guestPassword = guestCredentials?.password ?? null;
  // #659: 스냅샷 배열은 ref 로만 추적 (참조 변동 → 무한 fetch 방지). 재조회 트리거는
  // 별도 id 시그니처(localSnapshotIdsKey)로 — 새 주문 생성 시에만 변한다.
  const latestFetchConfigRef = useRef({
    mode,
    guestPhone,
    guestPassword,
    localSnapshots,
  });

  // stale refetch 핸들이 실행되어도 최신 render 입력만 사용한다.
  useLayoutEffect(() => {
    latestFetchConfigRef.current = { mode, guestPhone, guestPassword, localSnapshots };
  });

  // #659: 새 로컬 주문(스냅샷 id 집합 변화) 시 재조회 — 주문 직후 /orders 반영.
  const localSnapshotIdsKey = (localSnapshots ?? [])
    .map((snapshot) => snapshot.id)
    .join(',');

  useLayoutEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      requestIdRef.current += 1;
    };
  }, []);

  const resetAuthRejectedPollingSuspension = useCallback(() => {
    backgroundAuthRejectionCountRef.current = 0;
    if (!authRejectedPollingSuspendedRef.current) return;
    authRejectedPollingSuspendedRef.current = false;
    setAuthRejectedPollingSuspended(false);
  }, []);

  useLayoutEffect(() => {
    requestIdRef.current += 1;
    foregroundRequestRef.current = null;
    pollingFetchInFlightRef.current = null;
    setOrders([]);
    setError(null);
    setAuthRequired(false);
    setLoading(true);
    resetAuthRejectedPollingSuspension();
  }, [mode, customerId, guestPhone, guestPassword, resetAuthRejectedPollingSuspension]);

  const recordBackgroundAuthRejection = useCallback(() => {
    backgroundAuthRejectionCountRef.current += 1;
    if (
      backgroundAuthRejectionCountRef.current < BACKGROUND_AUTH_REJECTION_POLLING_LIMIT ||
      authRejectedPollingSuspendedRef.current
    ) {
      return;
    }

    authRejectedPollingSuspendedRef.current = true;
    setAuthRejectedPollingSuspended(true);
    logger.warn(
      '[useActiveOrders] 백그라운드 폴링 인가 거절로 반복 폴링을 중단합니다.'
    );
  }, []);

  const fetchActive = useCallback(async (options: { background?: boolean } = {}) => {
    if (!isMountedRef.current) return;
    // P1 race 시정 (codex): 진입 즉시 requestId 증가 → 이전 inflight 결과를 무효화.
    // early-return 분기에서도 동일하게 적용되어야 stale 주입 차단.
    const currentRequestId = ++requestIdRef.current;
    const isCurrent = () => isMountedRef.current && currentRequestId === requestIdRef.current;
    const {
      mode: currentMode,
      guestPhone: currentGuestPhone,
      guestPassword: currentGuestPassword,
      localSnapshots: currentSnapshots,
    } = latestFetchConfigRef.current;

    const tableSession = getTableSessionFromStorage();

    // A 소스 가용성 — #647 I1 유지: 빈/공백 phone(전화 없는 테이블 게스트)은 guestLookup
    // 호출 자체를 차단(admin phone required 422 회피). #659: 더 이상 에러가 아니라
    // "자격증명 소스 부재" — 세션/스냅샷 소스로 폴백한다.
    const hasCredentialSource =
      currentMode === 'auth' ||
      (currentMode === 'guest' && !!currentGuestPhone?.trim() && !!currentGuestPassword);
    // B 소스 — 회원(auth)은 skip (codex P2): 회원 주문은 A 소스(GET /orders)가 전부
    // 권위 있게 커버하고, 타인/혼재 세션 토큰 잔존 시 admin
    // ResolvesTableSessionChargeAccess 가 403(IDOR 로깅)으로 차단하므로 호출 노이즈만
    // 생긴다. 비회원/disabled 만 세션 토큰 소스를 사용한다.
    // split 세션의 무범위 unpaid-postpay는 대표의 pay-all 전용이며, 개인 Bill이 생기면
    // 대표에게도 403이다. 활성 주문 탭은 분할 Bill SSOT가 아니므로 이 legacy 소스를
    // 아예 호출하지 않는다. exact bill 조회는 SessionChargePage만 담당한다.
    const comensalAccess = readComensalAccess();
    const hasCurrentSplitLedger = tableSession !== null
      && comensalAccess?.sessionToken === tableSession.token
      && comensalAccess.hasSplitLedger;
    const hasSessionSource = tableSession !== null
      && currentMode !== 'auth'
      && readSplitPaymentAvailable() !== true
      && !hasCurrentSplitLedger;
    const hasSnapshotSource = (currentSnapshots?.length ?? 0) > 0;

    // 비회원 인증 password — guestCredentials 우선, 부재 시 보존 password 폴백 (#659):
    // checkout 의 getOrCreateGuestPassword 가 항상 `olulo_guest_password` 에 보존하므로
    // (전화 없는 테이블 게스트 포함, #647 C2), guestCredentials 가 401/403 처리로
    // 비워졌어도 세션 소스(B)/스냅샷 검증(C)은 보존 password 로 계속 동작한다.
    const effectiveGuestPassword =
      currentMode === 'auth'
        ? undefined
        : (currentGuestPassword ?? getSavedGuestPassword() ?? undefined);

    if (!hasCredentialSource && !hasSessionSource && !hasSnapshotSource) {
      // 모든 소스 부재 — idle empty (자격증명 오안내 없는 중립 빈 상태, #659)
      if (isMountedRef.current) {
        setOrders([]);
        setLoading(false);
        setError(null);
        setAuthRequired(false);
      }
      return;
    }

    if (!options.background) {
      foregroundRequestRef.current = currentRequestId;
      setLoading(true);
      setError(null);
      setAuthRequired(false);
    }
    try {
      // A + B 소스 병렬 조회 — 한쪽 실패가 다른 쪽 결과를 막지 않는다(부분 성공 우선).
      const credentialPromise: Promise<Order[] | null> = hasCredentialSource
        ? currentMode === 'auth'
          ? fetchAuthActiveOrders(isCurrent)
          : orderApi
              .guestLookup({
                phone: currentGuestPhone!,
                password: currentGuestPassword!,
              })
              .then((response) => response.data ?? [])
        : Promise.resolve(null);

      // R5 인증: 비회원은 X-Guest-Password — guestCredentials 부재 시 보존 password 폴백.
      // (hasSessionSource 가 auth 모드를 이미 배제하므로 이 경로는 항상 비회원.)
      const sessionPromise: Promise<Order[] | null> = hasSessionSource
        ? tableSessionApi
            .getSessionUnpaidPostpay(tableSession!.token, effectiveGuestPassword)
            .then((response) => response.orders ?? [])
        : Promise.resolve(null);

      const [credentialResult, sessionResult] = await Promise.allSettled([
        credentialPromise,
        sessionPromise,
      ]);

      // A/B 실패 뒤의 진단 및 C 소스 요청도 최초 계정/요청의 수명 안에서만 실행한다.
      if (!isCurrent()) return;

      const credentialOrders =
        credentialResult.status === 'fulfilled' ? credentialResult.value : null;
      const sessionOrders = sessionResult.status === 'fulfilled' ? sessionResult.value : null;
      const credentialFailed = hasCredentialSource && credentialResult.status === 'rejected';
      const sessionFailed = hasSessionSource && sessionResult.status === 'rejected';

      if (credentialFailed) {
        logger.warn('[useActiveOrders] 자격증명 소스 조회 실패:', {
          diagnostic_marker: 'menu.orders.active-status.v1', operation: 'credential-read', error: safeErrorMetadata((credentialResult as PromiseRejectedResult).reason) ?? { type: 'Unknown' },
        });
      }
      if (sessionFailed) {
        logger.warn('[useActiveOrders] 테이블 세션 소스 조회 실패:', {
          diagnostic_marker: 'menu.orders.active-status.v1', operation: 'session-read', error: safeErrorMetadata((sessionResult as PromiseRejectedResult).reason) ?? { type: 'Unknown' },
        });
      }

      const serverOrders = [...(credentialOrders ?? []), ...(sessionOrders ?? [])];

      // C 소스: 자격증명 소스가 부재/실패일 때만 — 자격증명 조회가 성공하면 그 응답이
      // 해당 고객의 주문 전체를 권위 있게 커버하므로(서버 우선) 스냅샷 검증은 불필요.
      let verifiedLocalOrders: Order[] = [];
      let snapshotAuthRejectedCount = 0;
      const credentialSucceeded = hasCredentialSource && !credentialFailed;
      if (!credentialSucceeded && hasSnapshotSource) {
        const serverIds = new Set(serverOrders.map((order) => order.id));
        const candidates = selectLocalSnapshotCandidates(currentSnapshots!, serverIds);
        if (candidates.length > 0) {
          const verification = await verifyLocalSnapshots(candidates, effectiveGuestPassword, isCurrent);
          verifiedLocalOrders = verification.verified;
          snapshotAuthRejectedCount = verification.authRejectedCount;
        }
      }

      if (!isMountedRef.current || currentRequestId !== requestIdRef.current) return;

      const merged = keepActiveOrders([...serverOrders, ...verifiedLocalOrders]);

      // #659 codex Important: 표시물 0건의 원인이 인가 거절(401/403)이면 일반 빈
      // 상태/에러 대신 '인증 필요' 구분 안내 (silent empty 금지).
      // R3 시정: 자격증명 소스(auth GET /orders, guestLookup)의 401/403 도 포함 —
      // 그 경로 단독 실패가 일반 에러로 낙하해 안내가 누락되던 갭. 단 lockout(429)
      // 등 비-인가 실패는 isAuthRejection(401/403 한정)이 배제 — 기존 일반 에러 의미 보존.
      const credentialAuthRejected =
        credentialFailed && isAuthRejection((credentialResult as PromiseRejectedResult).reason);
      const sessionAuthRejected =
        sessionFailed && isAuthRejection((sessionResult as PromiseRejectedResult).reason);
      const hasAuthRejection =
        credentialAuthRejected || sessionAuthRejected || snapshotAuthRejectedCount > 0;

      if (options.background) {
        if (hasAuthRejection) {
          recordBackgroundAuthRejection();
        } else {
          resetAuthRejectedPollingSuspension();
        }
      }

      // 시도한 서버 소스 전부 실패 + 폴백 표시물도 없을 때만 에러 노출.
      const attemptedCount = (hasCredentialSource ? 1 : 0) + (hasSessionSource ? 1 : 0);
      const failedCount = (credentialFailed ? 1 : 0) + (sessionFailed ? 1 : 0);
      if (merged.length === 0 && hasAuthRejection) {
        if (options.background) return;
        setOrders([]);
        setError(null);
        setAuthRequired(true);
      } else if (attemptedCount > 0 && failedCount === attemptedCount && merged.length === 0) {
        if (options.background) return;
        setError(new Error('주문 목록을 불러오는데 실패했습니다'));
        setOrders([]);
        setAuthRequired(false);
      } else {
        setOrders(merged);
        setError(null);
        setAuthRequired(false);
      }
    } catch (err) {
      if (!isMountedRef.current || currentRequestId !== requestIdRef.current) return;
      logger.error('[useActiveOrders] fetch failed:', {
        diagnostic_marker: 'menu.orders.active-status.v1', operation: 'combined-read', error: safeErrorMetadata(err) ?? { type: 'Unknown' },
      });
      if (options.background) return;
      setError(err instanceof Error ? err : new Error('주문 목록을 불러오는데 실패했습니다'));
      setOrders([]);
      setAuthRequired(false);
    } finally {
      if (foregroundRequestRef.current === currentRequestId) foregroundRequestRef.current = null;
      // silent gate self-audit (박제): code-reviewer P0-B 권고("loading 항상 해제")
      // 적용 시 race 회귀 — mode 빠른 전환 도중 stale finally 가 active fetch 의
      // loading 을 false 로 끔(flash 발생). currentRequestId 가드 유지가 정답.
      // 고착 회피는 소스-전무 early-return 분기의 명시적 setLoading(false) 가 담당.
      // #705 codex: background fetch 는 loading 을 true 로 만든 적이 없으므로(위 432 가드)
      // finally 에서도 foreground loading 을 건드리지 않는다(폴링 중 loading flash 차단).
      if (!options.background && isMountedRef.current && currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [recordBackgroundAuthRejection, resetAuthRejectedPollingSuspension]);

  useEffect(() => {
    resetAuthRejectedPollingSuspension();
  }, [
    mode,
    guestPhone,
    guestPassword,
    localSnapshotIdsKey,
    resetAuthRejectedPollingSuspension,
    tableSessionRefreshVersion,
  ]);

  useEffect(() => {
    fetchActive();
  }, [fetchActive, mode, customerId, guestPhone, guestPassword, localSnapshotIdsKey]);

  const fetchInBackground = useCallback(() => {
    if (pollingFetchInFlightRef.current || foregroundRequestRef.current !== null) return;
    const request = Symbol();
    pollingFetchInFlightRef.current = request;
    void fetchActive({ background: true }).finally(() => {
      if (pollingFetchInFlightRef.current === request) pollingFetchInFlightRef.current = null;
    });
  }, [fetchActive]);

  const previousPollingEnabledRef = useRef(pollingEnabled);
  useEffect(() => {
    const wasPollingEnabled = previousPollingEnabledRef.current;
    previousPollingEnabledRef.current = pollingEnabled;

    if (!pollingEnabled || wasPollingEnabled) return;
    if (authRejectedPollingSuspendedRef.current) return;

    // #705 codex: 즉시 background fetch 도 interval tick 과 동일한 in-flight 가드를
    // 거쳐야 중첩 fetch 가 발생하지 않는다(전환 fetch 가 30s 내 미완 시 첫 tick 과 경쟁).
    fetchInBackground();
  }, [fetchInBackground, pollingEnabled]);

  // #659/#814: QR 스캔으로 세션 토큰이 바뀌거나 split 정책이 확정되면 소스를 재판정한다.
  // split 전환 직전 시작된 legacy 요청도 requestId 증가로 stale 결과가 무효화된다.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleSessionChange = () => {
      resetAuthRejectedPollingSuspension();
      setTableSessionRefreshVersion((version) => version + 1);
      fetchActive();
    };
    window.addEventListener(TABLE_SESSION_CHANGED_EVENT, handleSessionChange);
    window.addEventListener(COMENSAL_ACCESS_CHANGED_EVENT, handleSessionChange);
    return () => {
      window.removeEventListener(TABLE_SESSION_CHANGED_EVENT, handleSessionChange);
      window.removeEventListener(COMENSAL_ACCESS_CHANGED_EVENT, handleSessionChange);
    };
  }, [fetchActive, resetAuthRejectedPollingSuspension]);

  const pollActiveOrders = useCallback(() => {
    if (pollingFetchInFlightRef.current) return;
    if (authRejectedPollingSuspendedRef.current) return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;

    fetchInBackground();
  }, [fetchInBackground]);

  // #443: 마운트/세션 변경 시 1회 조회만 하던 활성 주문을 `/orders` 탭에서 주기 재검증.
  useEffect(() => {
    if (!pollingEnabled || pollIntervalMs === false || pollIntervalMs <= 0) return;
    if (typeof window === 'undefined') return;
    if (authRejectedPollingSuspended) return;

    const hasCredentialPollingSource =
      mode === 'auth' || (mode === 'guest' && !!guestPhone?.trim() && !!guestPassword);
    const hasSessionPollingSource = mode !== 'auth' && getTableSessionFromStorage() !== null;
    const hasSnapshotPollingSource = localSnapshotIdsKey.length > 0;

    if (
      !hasCredentialPollingSource &&
      !hasSessionPollingSource &&
      !hasSnapshotPollingSource
    ) {
      return;
    }

    const intervalId = window.setInterval(pollActiveOrders, pollIntervalMs);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [
    mode,
    guestPhone,
    guestPassword,
    localSnapshotIdsKey,
    pollActiveOrders,
    pollingEnabled,
    pollIntervalMs,
    authRejectedPollingSuspended,
    tableSessionRefreshVersion,
  ]);

  return { orders, loading, error, authRequired, refetch: fetchActive };
}
